import React, { useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import Header from './layout/Header';
import SideBar from './layout/sidebar/Sidebar';
import styled, { css } from 'styled-components';
import useWindowSize from '../hooks/useWindowSize';
import { BrowseItemCard } from '../features/marketplace/assets/marketplaceStyle';
import {
  useGetOnboardingChecklistQuery,
  useUpdateCompleteOnboardingStatusMutation,
} from '../features/dashboard/dashboardApiSlice';
import { TOTAL_PRELIVE_CHECKS, preLiveCheckList } from '../utils/Constants';
import { selectCurrentUser } from '../features/auth/authSlice';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import SubscriptionAlert from './components/SubscriptionAlert';
import { useLocation } from 'react-router-dom';
import { setSideBar } from './services/sideBar';
import { useDispatch } from 'react-redux';
import { isAuthorized } from '../utils/helper';

const AppOutlet = ({ children, ...props }) => {
  const dispatch = useDispatch();
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const { data: preLiveChecks, refetch: refetchPreLiveChecks } =
    useGetOnboardingChecklistQuery();
  const location = useLocation();

  const [updateCompleteOnboardingStatus] =
    useUpdateCompleteOnboardingStatusMutation();
  const subscriptionDetails = preLiveChecks?.floristSubscriptionDTO || {};
  const { completed, percentage, initialState } = (() => {
    let count = 0;
    const initialState = {};
    if (!preLiveChecks)
      return {
        completed: 0,
        percentage: 0,
        initialState,
      };
    preLiveCheckList.forEach((item, index) => {
      if (item.subItems) {
        item.subItems.forEach((subItem) => {
          if (subItem.status(preLiveChecks[subItem.selector])) {
            count += 1;
          }
        });
      } else {
        if (item.status(item.selector.map((k) => preLiveChecks[k]))) {
          count += 1;
        }
      }

      initialState[[index]] = !item.status(
        item.selector.map((i) => preLiveChecks?.[i]),
      );
    });
    const result = Math.floor((count / TOTAL_PRELIVE_CHECKS) * 100);

    if (!onboardingCompleted && preLiveChecks?.onboardingComplete)
      setOnboardingCompleted(true);

    return {
      completed: count,
      percentage: result,
      initialState,
    };
  })();

  const [gettingStartedOpen, setGettingStartedOpen] = useState(initialState);
  const [openSideMenu, setOpenSideMenu] = useState(true);
  const [expandMenu, setExpandMenu] = useState('');
  const windowSize = useWindowSize();
  const isMobile = useMemo(
    () => windowSize.width < 768 && windowSize.width !== 0,
    [windowSize.width],
  );
  const currentUser = useSelector(selectCurrentUser);
  const imagesGalleryState = useSelector((state) => state.imagesGallery);
  const appWidth = useSelector((state) => state.sideBar.appContainerWidth);

  const blocked = imagesGalleryState
    ? Object.values(imagesGalleryState)?.some((values) => values?.length)
    : false;

  const getContainerClass = () => {
    if (openSideMenu && !isMobile) {
      return 'openSidebarContainer';
    } else if (!openSideMenu && !isMobile) {
      return 'closeSidebar';
    } else if (openSideMenu && isMobile) {
      return 'mobile-opener';
    } else {
      return '';
    }
  };

  useEffect(() => {
    dispatch(setSideBar(openSideMenu));
  }, [openSideMenu]);
  useEffect(() => {
    if (
      !onboardingCompleted &&
      completed &&
      currentUser?.floristId &&
      completed === TOTAL_PRELIVE_CHECKS
    ) {
      updateCompleteOnboardingStatus({
        id: currentUser?.floristId,
        onboardingComplete: true,
      });
      setOnboardingCompleted(true);
    }
  }, [completed]);

  const handleBeforeUnload = (event) => {
    if (blocked) {
      // Cancel the event
      event.preventDefault();
      // Chrome requires returnValue to be set
      event.returnValue = '';
    }
  };

  useEffect(() => {
    // Add event listener when component mounts
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Remove event listener when component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [blocked]);

  return (
    <>
      <Helmet>
        <link
          href="https://fonts.googleapis.com/css2?family=Source+Sans+Pro:ital,wght@0,200;0,300;0,400;0,600;0,700;0,900;1,200;1,300;1,400;1,600;1,700;1,900&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@200;300;400;500;600;700;800;900&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@200;300;400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Gabarito:wght@400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@200;300;400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Roboto+Slab:wght@200;300;400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@200;300;400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Oswald:wght@200;300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,200;9..40,300;9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;1,100;1,200;1,300;1,400;1,500;1,600;1,700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Imperial+Script&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Oleo+Script:wght@400;700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Petit+Formal+Script&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Pinyon+Script&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Rouge+Script&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Style+Script&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Noto+Serif:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500;1,600;1,700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Spectral+SC:ital,wght@0,200;0,300;0,400;0,500;0,600;0,700;0,800;1,200;1,300;1,400;1,500;1,600;1,700;1,800&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Vollkorn:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,500;1,600;1,700;1,800;1,900&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Alegreya:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,500;1,600;1,700;1,800;1,900&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Rasa:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500;1,600;1,700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Besley:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,500;1,600;1,700;1,800;1,900&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Aleo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Montagu+Slab:opsz,wght@16..144,100;16..144,200;16..144,300;16..144,400;16..144,500;16..144,600;16..144,700&display=swap"
          rel="stylesheet"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <Container fluid>
        <div className="row">
          <StyledSideBarContainer
            className={`col-auto px-0 sidebar-main ${getContainerClass()}`}
          >
            <SideBar
              setOpenSideMenu={setOpenSideMenu}
              openSideMenu={openSideMenu}
              expandMenu={expandMenu}
              setExpandMenu={setExpandMenu}
              isMobile={isMobile}
              onboardingCompleted={
                onboardingCompleted || completed === TOTAL_PRELIVE_CHECKS
              }
            />
          </StyledSideBarContainer>
          <AppContainer
            appWidth={appWidth}
            opensidemenu={openSideMenu?.toString()}
            isMobile={isMobile}
            className="col px-0"
          >
            <Header
              setOpenSideMenu={setOpenSideMenu}
              openSideMenu={openSideMenu}
              setExpandMenu={setExpandMenu}
              isMobile={isMobile}
              completed={onboardingCompleted ? TOTAL_PRELIVE_CHECKS : completed}
              total={TOTAL_PRELIVE_CHECKS}
              percentage={percentage}
              gettingStartedOpen={gettingStartedOpen}
              setGettingStartedOpen={setGettingStartedOpen}
            />
            <Container fluid>
              <div className="py-2 pt-lg-0 pb-0 mb-md-0" {...props}>
                <SubscriptionAlert
                  subscriptionDetails={subscriptionDetails}
                  refetchPreLiveChecks={refetchPreLiveChecks}
                />
                {children}
              </div>
            </Container>
          </AppContainer>
        </div>
      </Container>
    </>
  );
};

const StyledSideBarContainer = styled.div`
  ${({ theme }) => css`
    @media (max-width: ${theme?.breakpoints?.md}px) {
      &.mobile-opener {
        position: fixed;
        top: 0;
        left: 0;
        width: 100% !important;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.57);
        z-index: 99999;
        .openSidebar {
          width: 85% !important;
        }
      }
    }
  `}
`;
const AppContainer = styled.div`
  background-color: ${({ theme }) => theme.primaryLight2};
  width: ${({ opensidemenu, isMobile, appWidth }) =>
    opensidemenu === 'true'
      ? isMobile
        ? 'calc(100% - 10%)'
        : appWidth
        ? appWidth
        : 'calc(100% - 283px) '
      : 'calc(100% - 82px)'};
  margin-left: auto;
  height: 100%;
  min-height: 100vh;
  ${({ opensidemenu }) =>
    opensidemenu !== 'true' &&
    css`
      ${BrowseItemCard} {
        @media screen and (max-width: 900px) {
          width: 50%;
          flex: 0 0 auto;
        }
        @media screen and (max-width: 660px) {
          width: 100%;
          flex: 0 0 auto;
        }
      }
    `}
`;

export default AppOutlet;
